import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormSection } from '@/components/forms/Form';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { Combobox } from '@/components/forms/Combobox';
import { useFormations, useSessions, useUpsertInscription } from '@/hooks/queries/useFormation';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUser } from '@/hooks/useUser';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useUpsertEcheance } from '@/hooks/queries/useEcheances';
import { formatDA, formatDateDZ } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import type {
  InscriptionFormationRow,
  InscriptionStatut,
  ModeReglement,
} from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Sous-segment IDs autorisés depuis la page parente */
  defaultSousSegmentIds?: string[];
  /** Session ID pré-sélectionnée (depuis la page session detail) */
  defaultSessionId?: string;
  editing: InscriptionFormationRow | null;
  onSaved: () => void;
}

export function InscriptionFormDialog({
  open,
  onOpenChange,
  defaultSousSegmentIds,
  defaultSessionId,
  editing,
  onSaved,
}: Props): JSX.Element {
  const { user } = useUser();
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));
  const upsert = useUpsertInscription();
  const upsertEcheance = useUpsertEcheance();

  const { data: formations = [] } = useFormations({
    sousSegmentIds: defaultSousSegmentIds,
    statut: 'actif',
  });
  const [formationId, setFormationId] = useState('');
  const formationIds = useMemo(() => formations.map((f) => f.id), [formations]);

  const { data: allSessions = [] } = useSessions({});
  const sessions = useMemo(() => {
    let list = allSessions.filter((s) =>
      ['ouverte_inscriptions', 'planifiee', 'complete'].includes(s.statut),
    );
    if (formationId) list = list.filter((s) => s.formation_id === formationId);
    else if (formationIds.length > 0)
      list = list.filter((s) => formationIds.includes(s.formation_id));
    return list;
  }, [allSessions, formationId, formationIds]);

  const [sessionId, setSessionId] = useState('');
  const sessionSelected = sessions.find((s) => s.id === sessionId);

  const { data: tiers = [] } = useTiers({
    entrepriseId: eurlKostGroup?.id ?? null,
    search: '',
  });

  const [tierId, setTierId] = useState('');
  const [source, setSource] = useState('Direct');
  const [prixFacture, setPrixFacture] = useState('');
  const [acomptePct, setAcomptePct] = useState('30');
  const [modePaiement, setModePaiement] = useState<ModeReglement>('virement');
  const [statut, setStatut] = useState<InscriptionStatut>('confirme');
  const [genererEcheances, setGenererEcheances] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setFormationId('');
      setSessionId(editing.session_id);
      setTierId(editing.tier_id);
      setSource(editing.source ?? 'Direct');
      setPrixFacture(String(editing.prix_facture));
      setAcomptePct(String(editing.acompte_pct));
      setModePaiement(editing.mode_paiement ?? 'virement');
      setStatut(editing.statut);
      setGenererEcheances(false);
      setNotes(editing.notes ?? '');
    } else {
      setFormationId('');
      setSessionId(defaultSessionId ?? '');
      setTierId('');
      setSource('Direct');
      setPrixFacture('');
      setAcomptePct('30');
      setModePaiement('virement');
      setStatut('confirme');
      setGenererEcheances(true);
      setNotes('');
    }
    setError(null);
  }, [open, editing, defaultSessionId]);

  // Auto-rempli prix depuis session
  useEffect(() => {
    if (sessionSelected && !editing && !prixFacture && sessionSelected.prix_session) {
      setPrixFacture(String(sessionSelected.prix_session));
    }
  }, [sessionSelected, editing, prixFacture]);

  const tierOptions = useMemo(
    () =>
      tiers.slice(0, 500).map((tt) => ({
        value: tt.id,
        label: `${tt.code ? tt.code + ' · ' : ''}${tt.raison_sociale}`,
        hint: tt.email ?? tt.telephone ?? undefined,
      })),
    [tiers],
  );

  const prix = Number(prixFacture) || 0;
  const acompteVal = (prix * Number(acomptePct)) / 100;
  const soldeVal = prix - acompteVal;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!sessionId) return setError('Session obligatoire');
    if (!tierId) return setError('Tiers / participant obligatoire');
    if (!sessionSelected) return setError('Session introuvable');
    if (prix <= 0) return setError('Prix > 0 obligatoire');

    try {
      const inscription = await upsert.mutateAsync({
        id: editing?.id,
        session_id: sessionId,
        tier_id: tierId,
        commercial_id: user?.id ?? null,
        source,
        prix_facture: prix,
        acompte_pct: Number(acomptePct),
        mode_paiement: modePaiement,
        statut,
        notes: notes || null,
      });

      // Génération auto des 2 échéances acompte + solde
      if (genererEcheances && !editing && eurlKostGroup) {
        const today = new Date().toISOString().slice(0, 10);
        const dateSession = sessionSelected.date_debut;
        // Acompte : J+7 ou veille de session si plus proche
        const acompteEch = new Date(
          Math.min(Date.now() + 7 * 86400000, new Date(dateSession).getTime() - 7 * 86400000),
        );
        const acompteIso = acompteEch.toISOString().slice(0, 10);
        // Solde : 7j avant début session
        const soldeEch = new Date(new Date(dateSession).getTime() - 7 * 86400000);
        const soldeIso = soldeEch.toISOString().slice(0, 10);

        try {
          if (acompteVal > 0) {
            const ec = await upsertEcheance.mutateAsync({
              entreprise_id: eurlKostGroup.id,
              sens: 'a_encaisser',
              tier_id: tierId,
              facture_ref: `FA-FORM-${inscription.reference}-AC`,
              date_emission: today,
              date_echeance: acompteIso,
              montant_initial: acompteVal,
              montant_initial_dzd: acompteVal,
              montant_ht: acompteVal,
              montant_ttc: acompteVal,
              net_a_payer: acompteVal,
              devise_code: 'DZD',
              taux_change: 1,
              conditions_paiement: '15j',
              mode_reglement: modePaiement,
              priorite: 'normale',
              motif: `Acompte ${acomptePct}% inscription ${inscription.reference}`,
            });
            await supabase
              .from('inscriptions_formation')
              .update({ acompte_echeance_id: ec.id })
              .eq('id', inscription.id);
          }
          if (soldeVal > 0) {
            const ec = await upsertEcheance.mutateAsync({
              entreprise_id: eurlKostGroup.id,
              sens: 'a_encaisser',
              tier_id: tierId,
              facture_ref: `FA-FORM-${inscription.reference}-SD`,
              date_emission: today,
              date_echeance: soldeIso,
              montant_initial: soldeVal,
              montant_initial_dzd: soldeVal,
              montant_ht: soldeVal,
              montant_ttc: soldeVal,
              net_a_payer: soldeVal,
              devise_code: 'DZD',
              taux_change: 1,
              conditions_paiement: 'reception',
              mode_reglement: modePaiement,
              priorite: 'normale',
              motif: `Solde inscription ${inscription.reference}`,
            });
            await supabase
              .from('inscriptions_formation')
              .update({ solde_echeance_id: ec.id })
              .eq('id', inscription.id);
          }
          toast.success(
            `Inscription créée + 2 échéances (acompte ${formatDA(acompteVal)} échéance ${formatDateDZ(acompteIso)}, solde ${formatDA(soldeVal)} échéance ${formatDateDZ(soldeIso)})`,
          );
        } catch (echErr) {
          toast.warning(
            `Inscription créée mais échéances non générées : ${(echErr as Error).message}`,
          );
        }
      } else {
        toast.success(editing ? 'Inscription mise à jour' : 'Inscription créée');
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Inscription ${editing.reference}` : 'Nouvelle inscription'}
      description="L'inscription génère automatiquement deux échéances dans le module Échéances : acompte + solde 7j avant le début de session."
      onSubmit={handleSubmit}
      isSubmitting={upsert.isPending || upsertEcheance.isPending}
      size="xl"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <FormSection title="Formation & session">
        <FormSelect
          label="Filtrer par formation (optionnel)"
          value={formationId}
          onChange={setFormationId}
          options={[
            { value: '', label: '— Toutes —' },
            ...formations.map((f) => ({ value: f.id, label: `${f.code} — ${f.titre}` })),
          ]}
        />
        <FormSelect
          label="Session"
          value={sessionId}
          onChange={setSessionId}
          required
          options={[
            { value: '', label: '— Sélectionner —' },
            ...sessions.map((s) => ({
              value: s.id,
              label: `${s.reference} · ${formatDateDZ(s.date_debut)} → ${formatDateDZ(s.date_fin)}`,
            })),
          ]}
        />
      </FormSection>

      <FormSection title="Participant / Client">
        <Combobox
          label="Tiers"
          value={tierId}
          onChange={setTierId}
          options={tierOptions}
          placeholder="Rechercher par nom, code, email…"
          required
          fullWidth
          resultCount={tierOptions.length}
        />
        <FormSelect
          label="Source acquisition"
          value={source}
          onChange={setSource}
          options={[
            { value: 'Direct', label: 'Direct' },
            { value: 'CRM', label: 'CRM (opportunité)' },
            { value: 'Site web', label: 'Site web' },
            { value: 'Salon', label: 'Salon' },
            { value: 'Recommandation', label: 'Recommandation' },
          ]}
        />
      </FormSection>

      <FormSection title="Tarification & paiement">
        <FormField
          label="Prix facturé (DZD)"
          type="number"
          step="0.01"
          min="0"
          value={prixFacture}
          onChange={(e) => setPrixFacture(e.target.value)}
          required
          hint={
            sessionSelected?.prix_session
              ? `Prix session standard : ${formatDA(Number(sessionSelected.prix_session))}`
              : undefined
          }
        />
        <FormField
          label="Acompte %"
          type="number"
          step="1"
          min="0"
          max="100"
          value={acomptePct}
          onChange={(e) => setAcomptePct(e.target.value)}
        />
        <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
          <div className="text-muted-foreground">Acompte calculé</div>
          <div className="num text-sm font-semibold">{formatDA(acompteVal)}</div>
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
          <div className="text-muted-foreground">Solde dû</div>
          <div className="num text-sm font-semibold">{formatDA(soldeVal)}</div>
        </div>
        <FormSelect
          label="Mode de paiement"
          value={modePaiement}
          onChange={(v) => setModePaiement(v as ModeReglement)}
          options={[
            { value: 'virement', label: 'Virement' },
            { value: 'cheque', label: 'Chèque' },
            { value: 'especes', label: 'Espèces' },
            { value: 'cb', label: 'Carte bancaire' },
            { value: 'mobile', label: 'Mobile' },
          ]}
        />
        <FormSelect
          label="Statut"
          value={statut}
          onChange={(v) => setStatut(v as InscriptionStatut)}
          options={[
            { value: 'pre_inscrit', label: 'Pré-inscrit' },
            { value: 'confirme', label: 'Confirmé' },
            { value: 'paye_partiel', label: 'Acompte payé' },
            { value: 'paye_total', label: 'Soldé' },
            { value: 'liste_attente', label: "Liste d'attente" },
          ]}
        />
        {!editing && (
          <label className="flex items-center gap-2 text-xs sm:col-span-2">
            <input
              type="checkbox"
              checked={genererEcheances}
              onChange={(e) => setGenererEcheances(e.target.checked)}
            />
            Générer automatiquement les 2 échéances (acompte J+7 + solde J-7 session)
          </label>
        )}
      </FormSection>

      <FormSection title="Notes">
        <FormTextarea
          label="Notes internes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          rows={2}
        />
      </FormSection>
    </FormDialog>
  );
}
